import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { User, Lock, Eye, EyeOff, LogIn, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const success = await login(formData.username.trim(), formData.password.trim());

      if (success) {
        toast.success('Login successful! Redirecting...');
        const storedUser = JSON.parse(sessionStorage.getItem('ecomm_notify_user'));
        if (storedUser?.role === 'admin') {
          navigate('/dashboard');
        } else if (storedUser?.role === 'pos') {
          navigate('/pos');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid username or password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F3F3F3]">
      {/* Amazon-style top bar */}
      <div className="bg-[#131921] py-4 px-6 flex items-center">
        <h1 className="text-xl font-bold tracking-tight">
          <span className="text-white">eComm</span><span className="text-[#f08804]">Notify</span>
        </h1>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[380px]">

          {/* Login Card */}
          <div className="bg-white rounded-lg border border-[#DDD] p-7 shadow-sm">
            <div className="mb-5">
              <h2 className="text-[22px] font-normal text-[#0F1111]">Sign in</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div>
                <label htmlFor="username" className="block text-[13px] font-bold text-[#0F1111] mb-1">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 text-sm border border-[#a6a6a6] rounded shadow-inner bg-white text-[#0F1111] placeholder-[#aaa] focus:outline-none focus:ring-2 focus:ring-[#e77600] focus:border-[#e77600] transition-colors"
                  placeholder="Enter your username"
                  required
                  autoFocus
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-[13px] font-bold text-[#0F1111] mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full px-3 pr-10 py-2 text-sm border border-[#a6a6a6] rounded shadow-inner bg-white text-[#0F1111] placeholder-[#aaa] focus:outline-none focus:ring-2 focus:ring-[#e77600] focus:border-[#e77600] transition-colors"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#555] hover:text-[#0F1111] transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Submit — Amazon yellow button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center gap-2 py-2 px-4 rounded-lg text-[13px] font-semibold text-[#0F1111] bg-gradient-to-b from-[#FFD814] to-[#F7CA00] border border-[#F2C200] hover:from-[#F7CA00] hover:to-[#e7ae00] focus:outline-none focus:ring-2 focus:ring-[#e77600] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative mt-6 mb-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#e7e7e7]" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-[11px] text-[#767676]">Marketplace Aggregator Platform</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 flex flex-col items-center gap-2">
            <span className="text-[10px] text-[#999] uppercase tracking-wider">A product by</span>
            <img src="/loombiz-logo.png" alt="Loombiz Consultancy" className="h-7 object-contain" />
            <p className="text-[11px] text-[#999]">
              &copy; {new Date().getFullYear()} Loombiz Consultancy. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
